import { Button } from "components/Button/Button";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import { ResourceConfig } from "config/config";
import {
    actions as resourceActions,
    actions as resourcesActions,
} from "containers/Resources/Resources.reducer";
import { Action } from "containers/Resources/components/CommonActions/Action";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    getReadOnlyOrDeleted,
    isDeleted,
    migrateDeprecationsActionSignature,
    MigrateDeprecationsListAction,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    createWebhook,
    isCreateScheduleDisabled,
} from "containers/Resources/components/CommonActions/utils";
import {
    createScheduleAction,
    CreateScheduleDropdownContent,
    getAdditionalSchedulerList,
    getSchedulingBundleSchedulerId,
} from "containers/Resources/screens/ResourcesSingle/components/UsedByList/CreateScheduleButton";
import {
    commentsTab,
    inputSchemaTab,
    inputSchemaTabKey,
    logsTab,
    metadataTab,
    outputSchemaTab,
    referencesUsedByTab,
    usedBySchedulesTab,
    usedByWebhooksTab,
    valueAndSchemaInput,
    valueAndSchemaOutput,
    wrappersTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { executionPriorityColumn } from "containers/Search/components/SearchTabs/columns";
import restClient from "api/restClient";
import { convertTime } from "config/ts-utils";
import { useEffect, useState } from "react";
import { getRelativeUrl } from "utils/service";
import resource from "./resource";
import { RunWithOptions } from "./RunWithOptions";
import { getLatestExecutions } from "./sharedFields";
import {
    scriptField,
    valueAndSchemaInputField,
    valueAndSchemaOutputField,
} from "./sharedFieldsTyped";

type FlowStats = {
    p75_seconds: number | null;
    sample_count: number;
    success_rate: number | null;
    trend_ratio: number | null;
};

const FlowRuntimeStats = ({ flowId }: { flowId: string }) => {
    const [stats, setStats] = useState<FlowStats | null>(null);

    useEffect(() => {
        restClient
            .get(`/flow/${flowId}/runtime_stats`)
            .then((r) => setStats(r.data))
            .catch(() => {});
    }, [flowId]);

    if (!stats?.p75_seconds) return null;

    const { p75_seconds, sample_count, success_rate, trend_ratio } = stats;

    return (
        <div style={{ fontSize: "0.85em", lineHeight: 1.6 }}>
            <div>
                <span style={{ opacity: 0.8 }}>Typical runtime:</span>{" "}
                <strong>~{convertTime(p75_seconds)}</strong>
                <span style={{ opacity: 0.6, marginLeft: 6 }}>({sample_count} runs)</span>
            </div>
            {success_rate !== null && (
                <div>
                    <span style={{ opacity: 0.8 }}>Success rate:</span>{" "}
                    <strong
                        style={{
                            color:
                                success_rate >= 0.9
                                    ? "#2e7d32"
                                    : success_rate >= 0.7
                                    ? "#e65100"
                                    : "#c62828",
                        }}
                    >
                        {Math.round(success_rate * 100)}%
                    </strong>
                </div>
            )}
            {trend_ratio !== null && trend_ratio > 1.2 && (
                <div style={{ color: "#e65100" }}>▲ Running slower recently</div>
            )}
            {trend_ratio !== null && trend_ratio < 0.8 && (
                <div style={{ color: "#2e7d32" }}>▼ Running faster recently</div>
            )}
        </div>
    );
};

const removeAllBreakpointsAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: resourceActions.START_UPDATE_RESOURCE,
            promise: { resolve, reject },
            resourceName: "flow",
            variables: {
                id: data.resourceData?.id,
                breakpoints: [],
            },
        });
    }).then(() => {
        NotificationService.success("All breakpoints removed from flow");
    });
};

const runExecutionAction: Action = (
    data,
    dispatch,
    history,
    { isProductive, useBreakpoints, useExplicitBreakpoints, additionalVariables = {} }
) =>
    new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_CREATE_RESOURCE,
            promise: { resolve, reject },
            resourceName: "execution",
            variables: {
                type: "FLOW",
                flow_id: data.resourceData?.id,
                input_value: {},
                is_productive: isProductive,
                use_breakpoints: useBreakpoints,
                use_only_explicit_breakpoints: useExplicitBreakpoints,
                ...additionalVariables,
            },
        });
    }).then((newResourceData: any) => {
        if (newResourceData?.id) {
            history.push(getRelativeUrl("execution", newResourceData?.id));
        }
    });

const runAction: Action = (data, dispatch, history, additionalVariables) =>
    runExecutionAction(data, dispatch, history, {
        isProductive: true,
        useBreakpoints: false,
        additionalVariables,
    });

const tryAction: Action = (data, dispatch, history) =>
    runExecutionAction(data, dispatch, history, { isProductive: false, useBreakpoints: false });

const debugAction: Action = (data, dispatch, history) =>
    runExecutionAction(data, dispatch, history, { isProductive: false, useBreakpoints: true });

const debugFixedBreakpointsAction: Action = (data, dispatch, history) =>
    runExecutionAction(data, dispatch, history, {
        isProductive: false,
        useBreakpoints: true,
        useExplicitBreakpoints: true,
    });


const resourceName = "flow";
const config: ResourceConfig = {
    label: "Flow",
    icon: IconCollection.codeTags,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: inputSchemaTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [
                    inputSchemaTab.tab,
                    outputSchemaTab.tab,
                    wrappersTab.tab,
                    usedBySchedulesTab.tab,
                    usedByWebhooksTab.tab,
                    logsTab.tab,
                    commentsTab.tab,
                    referencesUsedByTab.tab,
                    metadataTab.tab,
                ],
                content: [
                    inputSchemaTab.content,
                    outputSchemaTab.content,
                    wrappersTab.content,
                    usedBySchedulesTab.content,
                    usedByWebhooksTab.content,
                    logsTab.content,
                    commentsTab.content,
                    referencesUsedByTab.content,
                    metadataTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [
            {
                key: "run",
                action: runAction,
                label: () => "Run",
                icon: () => IconCollection.play,
                disableWhen: isDeleted,
                isDropdown: () => true,
                dropdownContent: (props) => <RunWithOptions {...props} runAction={runAction} />,
            },
            // Added use_only_explicit_breakpoints  boolean flag to execution,
            //  setting that to true only injects breakpoints into the flow script
            //   where the user has set some. The step-into button thus acts exactly
            //    like the run button and should not be displayed by the UI if use_only_explicit_breakpoints  is true .
            {
                key: "try",
                action: tryAction,
                label: () => "Run in dev mode",
                icon: () => IconCollection.playSpeedIcon,
                disableWhen: isDeleted,
                isDropdown: () => true,
                dropdownContent: ({ actionData, dispatch, history, isDisabled, saveForm }) => (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                        }}
                    >
                        <Button
                            key={"debug"}
                            label={"Debug"}
                            icon={IconCollection.debugStepOver}
                            disabled={isDisabled}
                            variation="clear"
                            className="wide"
                            onClick={() =>
                                saveForm().then(() => debugAction(actionData, dispatch, history))
                            }
                        />
                        <Button
                            key={"debug-fixed"}
                            label={"Debug (fixed breakpoints)"}
                            icon={IconCollection.debugStepOver}
                            disabled={isDisabled}
                            variation="clear"
                            className="wide"
                            onClick={() =>
                                saveForm().then(() =>
                                    debugFixedBreakpointsAction(actionData, dispatch, history)
                                )
                            }
                        />
                        <Button
                            key={"removeAllBreakpoints"}
                            label={"Remove breakpoints"}
                            icon={IconCollection.circleOffOutline}
                            disabled={
                                getReadOnlyOrDeleted(actionData) ||
                                actionData.resourceData?.breakpoints?.length === 0
                            }
                            variation="clear"
                            className="wide"
                            onClick={() =>
                                removeAllBreakpointsAction(actionData, dispatch, history)
                            }
                        />
                    </div>
                ),
            },
            {
                key: "createSchedule",
                action: (data, dispatch, history) => {
                    const schedulingBundleSchedulerId = getSchedulingBundleSchedulerId(
                        data?.schedulerList
                    );
                    createScheduleAction({
                        recordId: data?.resourceData?.id,
                        schedulerId: schedulingBundleSchedulerId,
                        history,
                    });
                    return Promise.resolve(null);
                },
                label: () => "Create schedule",
                icon: () => IconCollection.timetable,
                disableWhen: (data) => isCreateScheduleDisabled(data?.resourceData),
                isDropdown: (actionData) =>
                    getAdditionalSchedulerList(actionData?.schedulerList).length > 0,
                dropdownContent: ({ actionData, dispatch, history, isDisabled, saveForm }) => {
                    return (
                        <CreateScheduleDropdownContent
                            isFieldDisabled={isDisabled}
                            action={(schedulerId) => {
                                createScheduleAction({
                                    recordId: actionData?.resourceData?.id,
                                    schedulerId,
                                    history,
                                });
                            }}
                            schedulerList={actionData?.schedulerList}
                        />
                    );
                },
            },
            {
                key: createWebhook.key,
                action: (data, dispatch, history) => {
                    createWebhook.action({ flowId: data?.resourceData?.id, history });
                    return Promise.resolve(null);
                },
                label: () => createWebhook.label,
                icon: () => createWebhook.icon,
                disableWhen: (data) => createWebhook.isDisabled(data?.resourceData),
            },
            migrateDeprecationsActionSignature,
        ],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
            { key: "migrateDeprecations", action: MigrateDeprecationsListAction },
        ],
    },
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            script
            input_schema
            output_schema
            breakpoints
            savepoint_retention
            webhook_list_flow_id (limit: 0, order: "+id", offset: 0) {
                webhook_count
            }
            schedule_list_flow_id (limit: 0, order: "+id", offset: 0) {
                schedule_count
            }
            priority
            max_runtime_minutes
            has_deprecation
            has_syntax_error
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            has_deprecation
            has_syntax_error
            priority
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $script: String,
            $input_schema: SchemalessData,
            $output_schema: SchemalessData,
            $breakpoints: [Int],
            $savepoint_retention: SavepointRetention,
            $priority: Int
            $max_runtime_minutes: Int
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            script: $script,
            input_schema: $input_schema,
            output_schema: $output_schema,
            breakpoints: $breakpoints,
            savepoint_retention: $savepoint_retention,
            priority: $priority
            max_runtime_minutes: $max_runtime_minutes
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $script: String
            $input_schema: SchemalessData,
            $output_schema: SchemalessData,
            $savepoint_retention: SavepointRetention,
            $breakpoints: [Int],
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            script: $script
            input_schema: $input_schema,
            output_schema: $output_schema,
            savepoint_retention: $savepoint_retention,
            breakpoints: $breakpoints,
        `,
    },
    additionalColumns: (setNewValue) => [executionPriorityColumn(setNewValue)],
    detailsSidebar: [
        {
            label: "Runtime estimate",
            valueKey: "",
            dto: (flow: any) => {
                if (!flow?.id) return null;
                return <FlowRuntimeStats flowId={flow.id} />;
            },
        },
    ],
    statsSidebar: [getLatestExecutions("flow")],
    form: [scriptField, valueAndSchemaInputField(), valueAndSchemaOutputField],
    formDTO: {
        initialValueDTO: (data: any) => {
            const valueAndSchemaInputFieldValues: SchematizedValues = {
                values: data?.input_value,
                schema: data?.input_schema,
            };
            const valueAndSchemaOutputFieldValues: SchematizedValues = {
                values: data?.output_value,
                schema: data?.output_schema,
            };
            return {
                ...data,
                [valueAndSchemaInput]: valueAndSchemaInputFieldValues,
                [valueAndSchemaOutput]: valueAndSchemaOutputFieldValues,
            };
        },
    },
};
export default config;
